import { useRef } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { docSource } from '../../../lib/doc-viewer';
import { ScrollArea, Typography } from '../ui';
import styles from './DocViewerModal.module.css';

// A video artifact — `<video controls preload="metadata">`, source resolved directly from `docSource`
// (remote only; see `ImageDocView`'s note on why this never goes through `useDocText`).
export function VideoDocView({
  content,
  fileName,
  onFirstPlay,
}: {
  content?: string | null;
  fileName: string;
  /** Fires once per mounted player, on the first play — the caller's own BI, so the surfaces can report
   *  under their own page names (mirrors `useAudioPlayer`'s first-play latch). */
  onFirstPlay?: () => void;
}) {
  const { t } = useTranslation();
  const source = docSource(content);
  const url = source?.kind === 'remote' ? source.url : null;

  // `onPlay` also fires on every resume after a pause; the ref keeps this to the one play that counts.
  // Keyed on the url so a player reused for a DIFFERENT file reports again — iOS spells the same guard
  // `.id(mediaURL)`. Today every host re-keys or re-mounts around this component, so the reset is a guard
  // against the next host rather than a live path.
  const reportedFor = useRef<string | null>(null);
  const handlePlay = () => {
    if (reportedFor.current === url) return;
    reportedFor.current = url;
    onFirstPlay?.();
  };

  if (!url) {
    return (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary" component="p">
          {t('docViewer.videoUnavailable')}
        </Typography>
      </div>
    );
  }

  return (
    <ScrollArea className={styles.mediaBody}>
      {/* An agent-produced artifact carries no captions track. */}
      <video controls preload="metadata" className={styles.mediaVideo} aria-label={fileName} onPlay={handlePlay}>
        <source src={url} />
      </video>
    </ScrollArea>
  );
}
